import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Schedule date rules shared by the create wizard and the edit modal.
 *
 * The schedule runs in one direction — participant deadline → presentation → first
 * coaching email — and each date must fall on or after the one before it. Coaching
 * defaults to a week after the presentation.
 *
 * The controls hold whatever bsDatepicker last wrote: a `Date` once the calendar has
 * been used, an `MM/DD/YYYY` string when typed. Everything here normalises first.
 */

/** Days between the presentation and the first coaching email, unless the user overrides it. */
export const COACHING_OFFSET_DAYS = 7;

/** Normalise a control value to a midnight Date, or null when empty/unparseable. */
export function toScheduleDate(value: unknown): Date | null {
  if (!value) { return null; }
  const d = value instanceof Date ? new Date(value.getTime()) : new Date(String(value));
  if (isNaN(d.getTime())) { return null; }
  d.setHours(0, 0, 0, 0);
  return d;
}

export function addDays(date: Date, days: number): Date {
  const d = new Date(date.getTime());
  d.setDate(d.getDate() + days);
  return d;
}

/**
 * The control's date must fall on or after `other`'s. Returns null when either side is
 * empty — emptiness belongs to the required validators, not the ordering rule.
 */
export function dateOnOrAfter(other: () => unknown, errorKey: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const mine = toScheduleDate(control.value);
    const theirs = toScheduleDate(other());
    if (!mine || !theirs) { return null; }
    return mine.getTime() < theirs.getTime() ? { [errorKey]: true } : null;
  };
}

/** As above, but the date must be strictly later — the same day fails. */
export function dateStrictlyAfter(other: () => unknown, errorKey: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const mine = toScheduleDate(control.value);
    const theirs = toScheduleDate(other());
    if (!mine || !theirs) { return null; }
    return mine.getTime() <= theirs.getTime() ? { [errorKey]: true } : null;
  };
}

/**
 * Midnight tomorrow — the earliest date that can satisfy "at least 24 hours in the future".
 *
 * The pickers are date-only, so this is the honest floor available to us: a date has no time
 * of day to measure 24 hours from. Tomorrow is at most 24h away and at least a few minutes,
 * depending on when the form is filled in; enforcing a true 24h minimum would mean rejecting
 * tomorrow entirely, which reads as a bug to anyone scheduling a day ahead.
 */
export function earliestCoachingDate(now: Date = new Date()): Date {
  const today = new Date(now.getTime());
  today.setHours(0, 0, 0, 0);
  return addDays(today, 1);
}

/** The date must be tomorrow or later. */
export function dateAtLeastTomorrow(errorKey: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const mine = toScheduleDate(control.value);
    if (!mine) { return null; }
    return mine.getTime() < earliestCoachingDate().getTime() ? { [errorKey]: true } : null;
  };
}

/** Required only while `when()` holds — used to demand a coaching date once the toggle is on. */
export function requiredWhen(when: () => boolean): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null =>
    when() && !control.value ? { required: true } : null;
}

/**
 * Apply the schedule rules to a form carrying `deadline`, `presentation`, `followupDate` and
 * `followupEmailsEnabled`. Used by both the create wizard and the edit modal, which share
 * `app-invitation-step-schedule` — and therefore share the note promising these rules.
 *
 * Call this on the component that owns the form, not the step component: the wizard *ngIf's its
 * steps, so a destroyed step-schedule would take its validators with it and let the gate pass.
 *
 * `coachingAlreadySet` seeds the "user has chosen a date" flag — pass true when loading an
 * invitation that already has one, so patching a presentation can't overwrite the stored date.
 */
export function wireScheduleRules(form: FormGroup, opts: { coachingAlreadySet?: boolean } = {}): void {
  const deadline = form.get('deadline')!;
  const presentation = form.get('presentation')!;
  const coaching = form.get('followupDate')!;
  const enabled = form.get('followupEmailsEnabled')!;

  let coachingEdited = !!opts.coachingAlreadySet;

  presentation.addValidators(dateOnOrAfter(() => deadline.value, 'beforeDeadline'));
  coaching.addValidators([
    requiredWhen(() => !!enabled.value),
    dateStrictlyAfter(() => presentation.value, 'notAfterPresentation'),
    dateAtLeastTomorrow('tooSoon'),
  ]);

  /** Default coaching to a week after the presentation, until the user picks their own date. */
  const autofill = () => {
    if (coachingEdited || !enabled.value) { return; }
    const p = toScheduleDate(presentation.value);
    if (!p) { return; }
    coaching.setValue(addDays(p, COACHING_OFFSET_DAYS), { emitEvent: false });
  };

  // A later date's validity depends on the one before it, so re-check downstream on every change.
  deadline.valueChanges.subscribe(() => presentation.updateValueAndValidity({ emitEvent: false }));
  presentation.valueChanges.subscribe(() => { autofill(); coaching.updateValueAndValidity({ emitEvent: false }); });
  enabled.valueChanges.subscribe(() => { autofill(); coaching.updateValueAndValidity({ emitEvent: false }); });

  // Only a real user edit reaches this — autofill() writes with emitEvent: false.
  coaching.valueChanges.subscribe(() => coachingEdited = true);
}
